import type { PriceMaskType } from '../../shared';

export const enum PriceCalendarTileType {
  WeekDay = 'weekDay',
  PreviousMonthDay = 'previousMonthDay',
  CurrentMonthPastDay = 'currentMonthPastDay',
  CurrentMonthAvailableDay = 'currentAvailableDay',
  CurrentMonthAlternativeProductDay = 'currentMonthAlternativeProductDay',
  CurrentMonthUnavailableDay = 'currentMonthUnavailableDay',
  CurrentMonthUnsearchedDay = 'currentMonthUnsearchedDay',
  CurrentMonthLoaderDay = 'currentMonthLoaderDay',
  NextMonthDay = 'nextMonthDay',
}

export interface WeekDayTile {
  type: PriceCalendarTileType.WeekDay;
  text: string;
}

export interface PreviousMonthDayTile {
  type: PriceCalendarTileType.PreviousMonthDay;
}

export interface CurrentMonthPastDayTile {
  type: PriceCalendarTileType.CurrentMonthPastDay;
  text: string;
  date: Date;
}

export interface TilePrice {
  type: PriceMaskType;
  value: string;
}

export const enum TileSelectionType {
  Suggested = 'suggested',
  Selected = 'selected',
  NotSelected = 'notSelected',
}

export interface CurrentMonthAvailableDayTile {
  type: PriceCalendarTileType.CurrentMonthAvailableDay;
  text: string;
  price: TilePrice;
  selection: TileSelectionType;
  date: Date;
}

export interface CurrentMonthAlternativeProductDayTile {
  type: PriceCalendarTileType.CurrentMonthAlternativeProductDay;
  text: string;
  reason: string;
  price: TilePrice | undefined;
  selection: TileSelectionType;
  date: Date;
}

export interface CurrentMonthUnavailableDayTile {
  type: PriceCalendarTileType.CurrentMonthUnavailableDay;
  text: string;
  reason: string;
  selection: TileSelectionType;
  date: Date;
}

export interface CurrentMonthUnsearchedDayTile {
  type: PriceCalendarTileType.CurrentMonthUnsearchedDay;
  text: string;
  selection: TileSelectionType;
  date: Date;
}

export interface CurrentMonthLoaderDayTile {
  type: PriceCalendarTileType.CurrentMonthLoaderDay;
  text: string;
  date: Date;
}

export interface NextMonthDayTile {
  type: PriceCalendarTileType.NextMonthDay;
}

export type CurrentMonthTile =
  | CurrentMonthPastDayTile
  | CurrentMonthAvailableDayTile
  | CurrentMonthAlternativeProductDayTile
  | CurrentMonthUnavailableDayTile
  | CurrentMonthUnsearchedDayTile
  | CurrentMonthLoaderDayTile;

export type PriceCalendarTile =
  | WeekDayTile
  | PreviousMonthDayTile
  | CurrentMonthTile
  | NextMonthDayTile;

export function isCurrentMonthTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthTile {
  return !(
    isWeekDayTile(calendarTile) ||
    isPreviousMonthDayTile(calendarTile) ||
    isNextMonthDayTile(calendarTile)
  );
}

export function isWeekDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is WeekDayTile {
  return calendarTile.type === PriceCalendarTileType.WeekDay;
}

export function isPreviousMonthDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is PreviousMonthDayTile {
  return calendarTile.type === PriceCalendarTileType.PreviousMonthDay;
}

export function isCurrentMonthPastDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthPastDayTile {
  return calendarTile.type === PriceCalendarTileType.CurrentMonthPastDay;
}

export function isCurrentMonthAvailableDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthAvailableDayTile {
  return calendarTile.type === PriceCalendarTileType.CurrentMonthAvailableDay;
}

export function isCurrentMonthAlternativeProductDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthAlternativeProductDayTile {
  return (
    calendarTile.type ===
    PriceCalendarTileType.CurrentMonthAlternativeProductDay
  );
}

export function isCurrentMonthUnavailableDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthUnavailableDayTile {
  return calendarTile.type === PriceCalendarTileType.CurrentMonthUnavailableDay;
}

export function isCurrentMonthUnsearchedDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthUnsearchedDayTile {
  return calendarTile.type === PriceCalendarTileType.CurrentMonthUnsearchedDay;
}

export function isCurrentMonthLoaderDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is CurrentMonthLoaderDayTile {
  return calendarTile.type === PriceCalendarTileType.CurrentMonthLoaderDay;
}

export function isNextMonthDayTile(
  calendarTile: PriceCalendarTile
): calendarTile is NextMonthDayTile {
  return calendarTile.type === PriceCalendarTileType.NextMonthDay;
}
